import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SidebarTrigger } from '../../components/ui/sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Loader2,
  Search,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MessageSquare,
} from 'lucide-react';
import {
  useTasks,
  useSetTaskStatus,
  useSetTaskAssignee,
  useTaskComments,
  useAddTaskComment,
} from '../../hooks/useTask';
import type {
  Task,
  TaskStatus,
  OrgResponsibility,
} from '../../services/task.service';
import { linearIssueUrl, startTaskDeepLink } from '../../services/task.service';
import { useUserConfig } from '../../hooks/useUserConfig';
import { useTenantConfig } from '../../hooks/useTenantConfig';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';

const STATUS_LABEL: Record<TaskStatus, string> = {
  triage: 'Triage',
  backlog: 'Backlog',
  unstarted: 'Todo',
  started: 'In Progress',
  completed: 'Done',
  canceled: 'Canceled',
};

const STATUS_ORDER: TaskStatus[] = [
  'triage',
  'backlog',
  'unstarted',
  'started',
  'completed',
  'canceled',
];

const STATUS_VARIANT: Record<TaskStatus, 'warning' | 'info' | 'default' | 'success' | 'secondary'> = {
  triage: 'warning',
  backlog: 'secondary',
  unstarted: 'info',
  started: 'default',
  completed: 'success',
  canceled: 'secondary',
};

const ORG_LABEL: Record<OrgResponsibility, string> = {
  PLIABLE: 'Pliable',
  TENANT: 'Tenant',
  END_USER: 'End User',
};

const ORG_VARIANT: Record<OrgResponsibility, 'default' | 'info' | 'secondary'> = {
  PLIABLE: 'default',
  TENANT: 'info',
  END_USER: 'secondary',
};

function StatusBadge({ status }: { status: TaskStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>;
}

function OrgBadge({ org }: { org: OrgResponsibility }) {
  return <Badge variant={ORG_VARIANT[org]}>{ORG_LABEL[org]}</Badge>;
}

const PAGE_SIZE = 25;

export default function Tasks() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [searchActive, setSearchActive] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const queryParams = useMemo(() => ({
    page,
    pageSize: PAGE_SIZE,
    status: statusFilter as any,
    orgResponsibility: orgFilter === 'all' ? undefined : (orgFilter as OrgResponsibility),
    owner: ownerFilter.trim() || undefined,
    search: searchActive || undefined,
  }), [page, statusFilter, orgFilter, ownerFilter, searchActive]);

  const { data, isLoading, error } = useTasks(queryParams);

  const records = data?.records ?? [];
  const totalPages = data?.total_pages ?? 1;
  const totalRecords = data?.total_records ?? 0;
  const selected = records.find((t) => t.id === selectedId) ?? null;

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchActive(searchInput.trim());
    setPage(1);
  };

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: '100vh' }}>
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <SidebarTrigger className="md:hidden h-5 w-5" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/develop">Develop</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Tasks</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <h1 className="text-xl font-semibold text-foreground">Tasks</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="triage">Triage</SelectItem>
              <SelectItem value="backlog">Backlog</SelectItem>
              <SelectItem value="unstarted">Todo</SelectItem>
              <SelectItem value="started">In Progress</SelectItem>
              <SelectItem value="completed">Done</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={orgFilter}
            onValueChange={(v) => {
              setOrgFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Responsibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All orgs</SelectItem>
              <SelectItem value="PLIABLE">Pliable</SelectItem>
              <SelectItem value="TENANT">Tenant</SelectItem>
              <SelectItem value="END_USER">End User</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="text"
            placeholder="Owner"
            value={ownerFilter}
            onChange={(e) => {
              setOwnerFilter(e.target.value);
              setPage(1);
            }}
            className="w-44"
          />
          <form onSubmit={onSearchSubmit} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search title..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-56 pl-9"
            />
          </form>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading tasks…</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">Failed to load tasks.</p>
          </div>
        ) : records.length === 0 ? (
          <div className="p-8 text-center">
            <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No tasks match the current filters.</p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44%]">Title</TableHead>
                    <TableHead className="w-[18%]">Type</TableHead>
                    <TableHead className="w-[22%]">Assignee</TableHead>
                    <TableHead className="w-[16%]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((item) => (
                    <TaskRow key={item.id} item={item} onOpen={() => setSelectedId(item.id)} />
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
              <div>
                {totalRecords === 0
                  ? '0 results'
                  : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalRecords)} of ${totalRecords}`}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <TaskDetailSheet task={selected} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function TaskRow({ item, onOpen }: { item: Task; onOpen: () => void }) {
  const description =
    item.description.length > 110 ? `${item.description.slice(0, 110)}…` : item.description;

  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onOpen}>
      <TableCell className="font-medium">
        <div className="truncate" title={item.title}>{item.title}</div>
        <div className="text-xs text-muted-foreground truncate mt-0.5">{description}</div>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground">{item.task_type}</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground truncate" title={item.owner || ''}>
          {item.owner || '—'}
        </span>
      </TableCell>
      <TableCell>
        <StatusBadge status={item.status} />
      </TableCell>
    </TableRow>
  );
}

function TaskDetailSheet({ task, onClose }: { task: Task | null; onClose: () => void }) {
  const open = !!task;
  const setStatus = useSetTaskStatus();
  const setAssignee = useSetTaskAssignee();
  const addComment = useAddTaskComment();
  const { data: userConfig } = useUserConfig();
  const { data: tenantConfig } = useTenantConfig();
  const isStaff = !!userConfig?.is_internal_staff;
  const hasIssue = !!task?.linear_issue_uuid;
  const { data: comments, isLoading: commentsLoading } = useTaskComments(
    task?.id ?? null,
    open && hasIssue,
  );

  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [commentDraft, setCommentDraft] = useState('');

  useEffect(() => {
    setAssigneeEmail(task?.owner ?? '');
    setCommentDraft('');
  }, [task?.id, task?.owner]);

  if (!task) {
    return (
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent className="sm:max-w-lg" />
      </Sheet>
    );
  }

  const submitComment = () => {
    const body = commentDraft.trim();
    if (!body) return;
    addComment.mutate({ id: task.id, body }, { onSuccess: () => setCommentDraft('') });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-lg overflow-y-auto flex flex-col gap-4">
        <SheetHeader>
          <SheetTitle className="pr-6">{task.title}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-wrap items-center gap-2">
          <OrgBadge org={task.org_responsibility} />
          <StatusBadge status={task.status} />
          <span className="text-xs text-muted-foreground">{task.task_type}</span>
          {task.category && (
            <span className="text-xs text-muted-foreground">· {task.category}</span>
          )}
          {task.linear_issue_id && !isStaff && (
            <span className="text-xs font-mono text-muted-foreground">· {task.linear_issue_id}</span>
          )}
          {task.linear_issue_id && isStaff && (
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-0.5 text-xs font-mono text-muted-foreground hover:text-foreground focus:outline-none">
                · {task.linear_issue_id}
                <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onClick={() => window.open(linearIssueUrl(task.linear_issue_id!), '_blank', 'noopener')}
                >
                  Open in Linear
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!userConfig?.stack || !tenantConfig?.tenant_slug}
                  onClick={() => {
                    if (!userConfig?.stack || !tenantConfig?.tenant_slug) return;
                    window.location.href = startTaskDeepLink(
                      userConfig.stack,
                      tenantConfig.tenant_slug,
                      task.linear_issue_id!,
                      task.title,
                    );
                  }}
                >
                  Start Task with Jimi
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {task.thread_id && (
            <Link
              to={`/ellie/conversation/${task.thread_id}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              title="Open the Ellie conversation that raised this request"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Thread
            </Link>
          )}
        </div>

        {task.description && (
          <p className="text-sm text-foreground whitespace-pre-wrap">{task.description}</p>
        )}

        <Separator />

        <div className="space-y-2">
          <Label>Status</Label>
          <Select
            value={task.status}
            onValueChange={(v) =>
              setStatus.mutate({ id: task.id, status: v as TaskStatus })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Assignee (email)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="email"
              placeholder="name@company.com"
              value={assigneeEmail}
              onChange={(e) => setAssigneeEmail(e.target.value)}
            />
            <Button
              variant="outline"
              disabled={setAssignee.isPending}
              onClick={() =>
                setAssignee.mutate({
                  id: task.id,
                  owner: assigneeEmail.trim() || null,
                  email: assigneeEmail.trim() || null,
                })
              }
            >
              Assign
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Matched to a Linear user by email; if there's no match it's kept in Pliable only.
          </p>
        </div>

        <Separator />

        <div className="space-y-3 flex-1">
          <Label className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Comments
          </Label>
          {!hasIssue ? (
            <p className="text-sm text-muted-foreground">
              This task has no linked Linear issue, so there are no comments to show.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <Textarea
                  placeholder="Add a comment…"
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    disabled={!commentDraft.trim() || addComment.isPending}
                    onClick={submitComment}
                  >
                    {addComment.isPending ? 'Posting…' : 'Comment'}
                  </Button>
                </div>
              </div>

              {commentsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading comments…
                </div>
              ) : (comments ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No comments yet.</p>
              ) : (
                <ul className="space-y-3">
                  {(comments ?? []).map((c) => (
                    <li key={c.id} className="rounded-md border border-border p-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-medium text-foreground">
                          {c.user?.displayName || c.user?.name || c.user?.email || 'Someone'}
                        </span>
                        {c.createdAt && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(c.createdAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{c.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
